import React, { useState, useEffect } from 'react';

import { API, authHeadersOnly } from '../services/apiConfig';

const STARS = [1, 2, 3, 4, 5];
const renderStars = (rating) =>
  STARS.map((s) => (
    <span key={s} style={{ color: s <= rating ? '#ffc107' : '#e0e0e0', fontSize: '14px' }}>★</span>
  ));

const ratingColor = (avg) => {
  if (avg >= 4) return '#22c55e';
  if (avg >= 3) return '#f59e0b';
  return '#ef4444';
};

export default function ShoppingExperienceFeedback() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('ALL');
  const [expanded, setExpanded]   = useState(false);

  useEffect(() => { fetchFeedbacks(); }, []);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/orders/feedback/all`, { headers: authHeadersOnly() });
      const data = res.ok ? await res.json() : [];
      setFeedbacks(Array.isArray(data) ? data : []);
    } catch { setFeedbacks([]); }
    setLoading(false);
  };

  const filtered = filter === 'ALL' ? feedbacks : feedbacks.filter(f => f.rating === parseInt(filter));
  const avg = feedbacks.length
    ? (feedbacks.reduce((s, f) => s + (f.rating || 0), 0) / feedbacks.length).toFixed(1)
    : null;

  const dist = [5, 4, 3, 2, 1].map(s => ({
    star: s,
    count: feedbacks.filter(f => f.rating === s).length,
    pct: feedbacks.length ? Math.round((feedbacks.filter(f => f.rating === s).length / feedbacks.length) * 100) : 0
  }));

  const SHOW = expanded ? filtered : filtered.slice(0, 5);

  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0', marginTop: '32px' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1a1a1a' }}>🛍️ Shopping Experience Feedback</h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#888' }}>Customer ratings submitted after placing an order</p>
        </div>
        <button onClick={fetchFeedbacks} style={{ background: '#f5f5f5', border: 'none', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#555' }}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>Loading...</div>
      ) : feedbacks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>
          <div style={{ fontSize: '36px', marginBottom: '8px' }}>💬</div>
          <p style={{ margin: 0 }}>No shopping experience feedback yet</p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div style={{ background: '#fafafa', borderRadius: '12px', padding: '20px 28px', textAlign: 'center', border: '1px solid #f0f0f0', minWidth: '130px' }}>
              <div style={{ fontSize: '40px', fontWeight: '800', color: ratingColor(parseFloat(avg)) }}>{avg}</div>
              <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }}>{renderStars(Math.round(avg))}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{feedbacks.length} response{feedbacks.length !== 1 ? 's' : ''}</div>
            </div>
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px' }}>
              {dist.map(({ star, count, pct }) => (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#555', width: '32px', textAlign: 'right' }}>{star} ★</span>
                  <div style={{ flex: 1, background: '#f0f0f0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#ffc107', borderRadius: '4px' }} />
                  </div>
                  <span style={{ fontSize: '12px', color: '#888', width: '24px' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Filter pills */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {['ALL', '5', '4', '3', '2', '1'].map(v => (
              <button key={v} onClick={() => { setFilter(v); setExpanded(false); }}
                style={{ padding: '5px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: filter === v ? '#232f3e' : '#f0f0f0',
                  color: filter === v ? 'white' : '#555' }}>
                {v === 'ALL' ? 'All' : `${v} ★`}
              </button>
            ))}
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f0f0f0', background: '#fafafa' }}>
                  {['#', 'Customer', 'Products', 'Order Amount', 'Rating', 'Feedback', 'Date'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#888', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SHOW.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#aaa' }}>No entries for this rating</td></tr>
                ) : SHOW.map((fb, idx) => (
                  <tr key={fb.id || idx} style={{ borderBottom: '1px solid #f8f8f8', transition: 'background 0.15s' }}
                    onMouseOver={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseOut={e => e.currentTarget.style.background = 'white'}>
                    <td style={{ padding: '12px 14px', color: '#ccc', fontSize: '12px' }}>{filtered.length - idx}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '700', color: '#1a1a1a' }}>{fb.userName || 'Guest'}</div>
                      {fb.userEmail && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{fb.userEmail}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555', maxWidth: '180px' }}>
                      {fb.productsPurchased || <span style={{ color: '#ccc', fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#22c55e', whiteSpace: 'nowrap' }}>
                      {fb.orderAmount ? `₹${Number(fb.orderAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {renderStars(fb.rating)}
                        <span style={{ fontWeight: '700', color: '#232f3e', marginLeft: '4px' }}>{fb.rating}/5</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#555', maxWidth: '260px' }}>
                      {fb.feedback || <span style={{ color: '#ccc', fontStyle: 'italic' }}>No comment</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#888', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {fb.createdAt ? new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length > 5 && (
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button onClick={() => setExpanded(e => !e)}
                style={{ background: 'none', border: '1.5px solid #e0e0e0', borderRadius: '8px', padding: '8px 24px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#555' }}>
                {expanded ? '▲ Show Less' : `▼ Show All ${filtered.length} Responses`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
